import React, { ChangeEvent, InputHTMLAttributes, useCallback, useEffect, useState } from 'react'
import { batch } from 'react-redux'
import { useDebounce, useWindowSize } from 'usehooks-ts'

import {
  jdihSetIsEventFilter,
  jdihSetIsEventFilterFirstTime,
  jdihSetKeyword,
  jdihSetSelected,
  jdihSetSkip,
  jdihSetTypeXid,
} from '@/reducers/jdih'
import clsxm from '@/utils/clsxm'
import { useAppDispatch, useAppSelector } from '@/utils/hooks'

import IcDismiss from '~/assets/icons/icon-dismiss.svg'
import IcSearch from '~/assets/icons/icon-search.svg'

type SearchBarProps = InputHTMLAttributes<HTMLInputElement>

const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(({ ...rest }, ref) => {
  const dispatch = useAppDispatch()
  const [keyword, setKeyword] = useState('')
  const { width } = useWindowSize()
  const keywordDebounce = useDebounce(keyword, width <= 768 ? 1000 : 500)
  const jdihKeyword = useAppSelector((state) => state.Jdih.keyword)

  const handleChangeSearch = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setKeyword(value)
    dispatch(jdihSetIsEventFilterFirstTime(true))
  }
  const reset = () => {
    batch(() => {
      dispatch(jdihSetIsEventFilter(true))
      dispatch(jdihSetSkip(0))
      dispatch(jdihSetSelected(0))
    })
  }

  const handleResetSearch = useCallback(() => {
    dispatch(jdihSetKeyword(''))
    reset()
  }, [])

  useEffect(() => {
    dispatch(jdihSetKeyword(keywordDebounce))
    dispatch(jdihSetTypeXid(''))
    reset()
  }, [keywordDebounce])

  useEffect(() => {
    setKeyword(jdihKeyword)
  }, [jdihKeyword])

  return (
    <div className='relative w-full rounded-lg'>
      <input
        ref={ref}
        {...rest}
        placeholder={rest.placeholder}
        className={clsxm(
          'w-full rounded-lg border border-neutral-border bg-white py-2 pl-3 pr-12 lg:px-3 lg:py-2.5',
          'flex-1 text-dark',
          'placeholder:text-sm placeholder:font-medium placeholder:text-neutral-placeholder focus:outline-0 focus:ring-0',
          'placeholder:line-clamp-1',
          rest.className
        )}
        onChange={handleChangeSearch}
        value={keyword}
      />

      <div
        className={clsxm(
          'absolute inset-y-0 right-0 flex items-center justify-center pr-3 lg:pr-3',
          jdihKeyword.length > 0 ? 'cursor-pointer' : 'pointer-events-none'
        )}
        onClick={jdihKeyword.length > 0 ? handleResetSearch : () => null}
      >
        {jdihKeyword.length > 0 ? (
          <IcDismiss className={clsxm('block h-6 w-6')} />
        ) : (
          <IcSearch className={clsxm('block h-6 w-6')} />
        )}
      </div>
    </div>
  )
})

export default SearchBar
